import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Image as ImageIcon, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 200;

/**
 * Reusable Media Library picker. Pass it `open` + `onOpenChange` and a
 * `onSelect(item)` callback. The selected item shape is `{ id, url, filename }`.
 *
 * Used from the Add Team / Add League / Add Brand forms so users can attach a
 * logo from the Media Library without re-uploading.
 */
export function MediaPicker({ open, onOpenChange, onSelect, title = "Pick a logo from Media Library" }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchPage = useCallback(async (skip, append) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const { data } = await api.get("/media", { params: { skip, limit: PAGE_SIZE } });
      setItems((prev) => (append ? [...prev, ...(data.items || [])] : (data.items || [])));
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load media");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setSearch("");
      fetchPage(0, false);
    }
  }, [open, fetchPage]);

  const filtered = search
    ? items.filter((it) => it.filename.toLowerCase().includes(search.toLowerCase()))
    : items;

  const hasMore = !search && items.length < total;

  const handleConfirm = () => {
    if (!selected) {
      toast.error("Pick a logo first");
      return;
    }
    onSelect?.(selected);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl flex flex-col max-h-[85vh] p-0" data-testid="media-picker-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>
            Browse {total} file{total === 1 ? "" : "s"} from your Media Library and click to pick one.
            Need to add new logos? Upload them from the <strong>Media</strong> page first.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 space-y-3 min-h-0">
          <div className="relative pt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search filename..."
              className="pl-9"
              data-testid="media-picker-search"
            />
          </div>

          {loading ? (
            <div className="text-center text-sm text-muted-foreground py-12">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {items.length === 0
                  ? "No media yet — upload some from the Media page first."
                  : "No files match your search."}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2 pb-2">
                {filtered.map((it) => {
                  const isSelected = selected?.id === it.id;
                  return (
                    <button
                      type="button"
                      key={it.id}
                      onClick={() => setSelected(it)}
                      onDoubleClick={() => {
                        setSelected(it);
                        setTimeout(() => onSelect?.(it), 0);
                        onOpenChange?.(false);
                      }}
                      className={cn(
                        "group rounded-lg border bg-card p-1.5 transition-all text-left",
                        isSelected
                          ? "border-primary ring-2 ring-primary/40"
                          : "border-border hover:border-primary/40",
                      )}
                      data-testid={`media-picker-tile-${it.id}`}
                    >
                      <div className="aspect-square bg-secondary/30 rounded flex items-center justify-center p-1.5 mb-1">
                        <img
                          src={mediaUrl(it.url)}
                          alt={it.filename}
                          className="max-w-full max-h-full object-contain"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <p className="text-[10px] truncate" title={it.filename}>{it.filename}</p>
                    </button>
                  );
                })}
              </div>
              {hasMore && (
                <div className="flex justify-center pt-1 pb-2">
                  <Button
                    variant="outline" size="sm"
                    disabled={loadingMore}
                    onClick={() => fetchPage(items.length, true)}
                  >
                    {loadingMore ? "Loading…" : `Load more (${items.length}/${total})`}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-border bg-background flex-shrink-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} size="sm">
            Cancel
          </Button>
          <Button
            type="button" variant="premium" size="sm"
            disabled={!selected}
            onClick={handleConfirm}
            data-testid="media-picker-confirm"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Use this logo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
